import type { ApolloClient } from '@apollo/client';
import {
  ME_QUERY,
  GET_COMPANY_INFO,
  type MeQueryResult,
  type GetCompanyInfoQueryResult,
} from '@scio3/graphql-client';

/** i18n key shown when the company enforces SSO and the sign-in method isn't allowed (#2491). */
export const SSO_REQUIRED_MESSAGE =
  "Your organization requires single sign-on. Please sign in with your organization's SSO provider.";

/** i18n key naming the enforced provider — interpolate `provider` with the label (#2491). */
export const SSO_REQUIRED_PROVIDER_MESSAGE =
  'Your organization requires single sign-on. Please sign in with {{provider}}.';

export type SsoProvider = 'google' | 'microsoft';

const SSO_PROVIDER_LABELS: Record<SsoProvider, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Narrow an untrusted value (error extension, storage, event detail) to a known SSO provider. */
export function toSsoProvider(value: unknown): SsoProvider | undefined {
  return value === 'google' || value === 'microsoft' ? value : undefined;
}

/**
 * The enforced provider carried by an SSO_REQUIRED GraphQL error at
 * `extensions.exception.response.provider`. Reads Apollo 4 `errors` and the v3 `graphQLErrors`;
 * returns the first known provider across the given errors, or undefined.
 */
export function getSsoRequiredProvider(...errors: unknown[]): SsoProvider | undefined {
  for (const error of errors) {
    if (!isRecord(error)) continue;
    const list = Array.isArray(error.errors)
      ? error.errors
      : Array.isArray(error.graphQLErrors)
        ? error.graphQLErrors
        : [];
    for (const item of list) {
      if (!isRecord(item) || item.message !== 'SSO_REQUIRED') continue;
      const exception = isRecord(item.extensions) ? item.extensions.exception : undefined;
      const response = isRecord(exception) ? exception.response : undefined;
      const provider = toSsoProvider(isRecord(response) ? response.provider : undefined);
      if (provider) return provider;
    }
  }
  return undefined;
}

/** i18n key + params for the SSO_REQUIRED notice — names the provider when known, else generic. */
export function ssoRequiredNotice(provider: unknown): {
  key: string;
  params?: { provider: string };
} {
  const known = toSsoProvider(provider);
  return known
    ? { key: SSO_REQUIRED_PROVIDER_MESSAGE, params: { provider: SSO_PROVIDER_LABELS[known] } }
    : { key: SSO_REQUIRED_MESSAGE };
}

interface ValidateResult {
  valid: boolean;
  /** i18n key or error message — caller should pass through t(error, errorParams) */
  error?: string;
  /** Interpolation params for `error` (e.g. the enforced SSO provider label) */
  errorParams?: { provider: string };
  /** True when the sign-in was rejected because the company enforces SSO (#2491) */
  isSsoRequired?: boolean;
}

/**
 * Validate that the authenticated user belongs to a team.
 * Shared across LoginPage, SignupPage, and SAML flows.
 *
 * Queries ME + Company with the provided token. Returns:
 * - { valid: true } if user has teams
 * - { valid: false, error } if INVALID_TEAM, NOT_FOUND_USER (without orphan recovery), or empty teams
 *
 * Caller is responsible for calling fullLogout() and setError() on failure.
 */
export async function validateTeamAccess(
  client: ApolloClient,
  token: string,
  { allowOrphanRecovery = false }: { allowOrphanRecovery?: boolean } = {},
): Promise<ValidateResult> {
  try {
    const authHeaders = { Authorization: `Bearer ${token}` };
    const [meResult, companyResult] = await Promise.allSettled([
      client.query<MeQueryResult>({
        query: ME_QUERY,
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
        context: { headers: authHeaders },
      }),
      client.query<GetCompanyInfoQueryResult>({
        query: GET_COMPANY_INFO,
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
        context: { headers: authHeaders },
      }),
    ]);

    const meError = meResult.status === 'fulfilled' ? meResult.value.error?.message : undefined;
    const companyError =
      companyResult.status === 'fulfilled' ? companyResult.value.error?.message : undefined;

    // SSO_REQUIRED: the user's company enforces SSO and this sign-in method isn't allowed (#2491)
    if (meError?.includes('SSO_REQUIRED') || companyError?.includes('SSO_REQUIRED')) {
      const notice = ssoRequiredNotice(
        getSsoRequiredProvider(
          meResult.status === 'fulfilled' ? meResult.value.error : undefined,
          companyResult.status === 'fulfilled' ? companyResult.value.error : undefined,
        ),
      );
      return { valid: false, error: notice.key, errorParams: notice.params, isSsoRequired: true };
    }

    // INVALID_TEAM: user exists but belongs to no teams
    if (meError?.includes('INVALID_TEAM') || companyError?.includes('INVALID_TEAM')) {
      return {
        valid: false,
        error: 'You do not belong to a team. Please contact your account Administrator.',
      };
    }

    // NO_BRANDING_ACCESS: white-label login gate — the account isn't branding-entitled, or doesn't
    // own/inherit the custom domain it's logging in on. SCIO 1.0 no_acess_msg (accountConfigurations.js).
    if (meError?.includes('NO_BRANDING_ACCESS') || companyError?.includes('NO_BRANDING_ACCESS')) {
      return {
        valid: false,
        error:
          'You are not a member of this organization. Please contact your account Administrator.',
      };
    }

    // NOT_FOUND_USER: Firebase user exists but no MongoDB user
    if (meError?.includes('NOT_FOUND_USER')) {
      if (allowOrphanRecovery) {
        return { valid: true };
      }
      return { valid: false, error: 'User not found.' };
    }

    const meData = meResult.status === 'fulfilled' ? meResult.value.data?.me : undefined;

    // Null data without INVALID_TEAM — likely a schema/data issue
    if (!meData && !meError?.includes('INVALID_TEAM')) {
      const graphqlErrors = meResult.status === 'fulfilled' ? meResult.value.error : undefined;
      console.error('Login failed: ME_QUERY returned null data', graphqlErrors);
      return { valid: false, error: 'Login failed. Please try again or contact support.' };
    }

    // Empty teams array
    if (!meData?.teams || meData.teams.length === 0) {
      return {
        valid: false,
        error: 'You do not belong to a team. Please contact your account Administrator.',
      };
    }

    return { valid: true };
  } catch {
    // Network/other errors — let AuthProvider handle it
    return { valid: true };
  }
}
